import { ConditionType, Field, FieldControllerType } from '@gemini/lux-ds-pms';
import { CustomTranslationFnc } from '@hooks/useCustomTranslation';
import { CountryListDTO, MasterLookUpDTO } from '@utils/interfaces';

const patientDetailsForm = (
  t: CustomTranslationFnc,
  options?: {
    countryList: CountryListDTO;
    countryStatesList: CountryListDTO;
    genderList: MasterLookUpDTO;
    readOnlyPrefilledPatientsDetailsFields?: boolean;
    prefilledPatientDetailsFields?: {
      firstName?: boolean;
      lastName?: boolean;
      dateOfBirth?: boolean;
      email?: boolean;
      phoneNumber?: boolean;
    };
  },
  isBranded?: boolean, //this param is used to customize some fields based on brand presence
): Field[] => [
  {
    id: 'patientDetails.firstName',
    labelKey: 'fieldsLabel.firstName',
    key: 'string', // key per masterLookUp
    type: FieldControllerType.TEXT,
    visibility: true,
    disabled:
      !!options?.readOnlyPrefilledPatientsDetailsFields &&
      !!options?.prefilledPatientDetailsFields?.firstName,
    size: {
      xs: 12,
      md: 6,
      lg: 6,
    },
    validations: [
      {
        type: 'regex',
        value: /^[A-Za-z\s]+$/,
        errorMsgKey: 'fieldsLabel.errors.onlyLetters',
      },
    ],
    otherOptions: {
      text: {
        hideStatusAdornments: isBranded,
      },
    },
  },
  {
    id: 'patientDetails.lastName',
    labelKey: 'fieldsLabel.lastName',
    key: 'string', // key per masterLookUp
    type: FieldControllerType.TEXT,
    visibility: true,
    disabled:
      !!options?.readOnlyPrefilledPatientsDetailsFields &&
      !!options?.prefilledPatientDetailsFields?.lastName,
    size: {
      xs: 12,
      md: 6,
      lg: 6,
    },
    validations: [
      {
        type: 'regex',
        value: /^[A-Za-z\s]+$/,
        errorMsgKey: 'fieldsLabel.errors.onlyLetters',
      },
    ],
    otherOptions: {
      text: {
        hideStatusAdornments: isBranded,
      },
    },
  },
  {
    id: 'patientDetails.gender',
    labelKey: 'fieldsLabel.gender',
    type: FieldControllerType.DROPDOWN,
    visibility: true,
    disabled: false,
    size: {
      xs: 12,
      md: 6,
      lg: 3,
    },
    options: options
      ? options.genderList.map(gender => ({
          key: gender.itemId,
          label: gender.itemName,
        }))
      : [],
  },
  {
    id: 'patientDetails.dateOfBirth',
    labelKey: 'fieldsLabel.dob',
    key: 'string', // key per masterLookUp
    type: FieldControllerType.DATEPICKER,
    visibility: true,
    disabled:
      !!options?.readOnlyPrefilledPatientsDetailsFields &&
      !!options?.prefilledPatientDetailsFields?.dateOfBirth,
    otherOptions: {
      datePicker: {
        disableFuture: true,
        minDate: new Date(1753, 0, 1),
        maxDate: new Date(),
        msgInvalid: t('fieldsLabel.errors.dateFormat'),
        msgMinDate: t('fieldsLabel.errors.dobInvalid'),
        msgMaxDate: t('fieldsLabel.errors.dateInFuture'),
        isMonthPicker: true,
        pickDay: true,
      },
    },
    size: {
      xs: 12,
      md: 6,
      lg: 3,
    },
  },
  {
    id: 'patientDetails.email',
    labelKey: 'fieldsLabel.email',
    key: 'string', // key per masterLookUp
    type: FieldControllerType.TEXT,
    visibility: true,
    disabled:
      !!options?.readOnlyPrefilledPatientsDetailsFields &&
      !!options?.prefilledPatientDetailsFields?.email,
    size: {
      xs: 12,
      md: 6,
      lg: 6,
    },
    validations: [
      // required gestito da servizio in maniera statica
      {
        type: 'regex',
        value:
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/,
        errorMsgKey: 'fieldsLabel.errors.email',
      },
    ],
    otherOptions: {
      text: {
        hideStatusAdornments: isBranded,
      },
    },
  },
  {
    id: 'patientDetails.phone',
    // labelKey: 'fieldsLabel.phoneNumber',
    key: 'string', // key per masterLookUp
    type: FieldControllerType.GROUP,
    visibility: true,
    disabled: false,
    size: {
      xs: 12,
      md: 12,
      lg: 12,
    },
    children: [
      {
        id: 'patientDetails.phone.number',
        labelKey: 'fieldsLabel.phoneNumber',
        otherOptions: {
          phone: {
            prefixId: 'patientDetails.phone.prefix',
            countryId: 'patientDetails.phone.country',
            showPrefixAsText: true,
            prefixHasClearIcon: false,
          },
          text: {
            hideStatusAdornments: isBranded,
          },
        },
        // key: 'string', // key per masterLookUp
        type: FieldControllerType.PHONE,
        visibility: true,
        disabled:
          !!options?.readOnlyPrefilledPatientsDetailsFields &&
          !!options?.prefilledPatientDetailsFields?.phoneNumber,
        size: {
          xs: 12,
          md: 12,
          lg: 8,
        },
        validations: [
          // required gestito da servizio in maniera statica
          {
            type: 'validate',
            value: 0,
            errorMsgKey: 'fieldsLabel.errors.phoneInvalid',
          },
        ],
      },
      {
        id: 'patientDetails.phone.type',
        key: 'string', // key per masterLookUp
        type: FieldControllerType.RADIO,
        visibility: false,
        disabled: false,
        size: {
          xs: 12,
          md: 12,
          lg: 4,
        }, // 6 per metà, 12 intero
        options: [
          { key: 'mobile', label: 'fieldsLabel.mobile' },
          { key: 'landline', label: 'fieldsLabel.landline' },
        ],
        interdepCondition: {
          toEvaluateIf: {
            type: 'required',
            value: false,
            errorMsgKey: 'error',
          },
          conditions: [
            {
              idRef: 'patientDetails.phone.number',
              type: ConditionType.EMPTY,
            },
          ],
        },
      },
    ],
  },
  {
    id: 'patientDetails.streetAddress',
    labelKey: 'fieldsLabel.streetAddress',
    key: 'string', // key per masterLookUp
    type: FieldControllerType.TEXT,
    otherOptions: {
      text: {
        maxLength: 55,
      },
    },
    visibility: true,
    disabled: false,
    size: {
      xs: 12,
      md: 6,
      lg: 6,
    },
  },
  {
    id: 'patientDetails.city',
    labelKey: 'fieldsLabel.city',
    key: 'string', // key per masterLookUp
    type: FieldControllerType.TEXT,
    otherOptions: {
      text: {
        maxLength: 50,
      },
    },
    validations: [
      {
        type: 'regex',
        value: /^[a-zA-Z0-9' -]+$/,
        errorMsgKey: 'fieldsLabel.errors.noSpecialChars',
      },
    ],
    visibility: true,
    disabled: false,
    size: {
      xs: 12,
      md: 6,
      lg: 6,
    },
  },
  {
    id: 'patientDetails.zipCode',
    labelKey: 'fieldsLabel.zipCode',
    key: 'string', // key per masterLookUp
    type: FieldControllerType.TEXT,
    visibility: true,
    disabled: false,
    size: {
      xs: 6,
      md: 3,
      lg: 3,
    },
    validations: [
      {
        type: 'regex',
        value: /^[0-9]{5}(-[0-9]{4})?$/,
        errorMsgKey: 'fieldsLabel.errors.zipCode',
      },
    ],
  },
  {
    id: 'patientDetails.state',
    labelKey: 'fieldsLabel.state',
    type: FieldControllerType.DROPDOWN,
    visibility: true,
    disabled: false,
    size: {
      xs: 6,
      md: 3,
      lg: 3,
    },
    options: options
      ? [
          ...options.countryStatesList.map(state => ({
            key: state.code,
            label: state.name,
          })),
        ]
      : [],
  },
  {
    id: 'patientDetails.country',
    labelKey: 'fieldsLabel.country',
    type: FieldControllerType.DROPDOWN,
    visibility: true,
    disabled: true,
    size: {
      xs: 12,
      md: 6,
      lg: 6,
    },
    options: options
      ? [
          ...options.countryList.map(country => ({
            key: country.code,
            label: country.name,
          })),
        ]
      : [],
  },
  {
    id: 'patientDetails.customField',
    labelKey: 'fieldsLabel.customField',
    type: FieldControllerType.TEXT,
    visibility: false,
    disabled: false,
    size: {
      xs: 12,
      md: 6,
      lg: 6,
    },
  },
];

export default patientDetailsForm;
